import React, { useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { ConfigProvider, Layout } from 'antd';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './redux/hooks';
import { clearAuth, checkSession } from './redux/reducers/authSlice';
import LoginScreen from './screens/Login/LoginScreen';
import SsoRedirectingScreen from './screens/Login/SsoRedirectingScreen';
import AuthCallbackScreen from './screens/Login/AuthCallbackScreen';
import SsoUnavailableScreen from './screens/Login/SsoUnavailableScreen';
import HomeScreen from './screens/Home/HomeScreen';
import { IncidentCreationScreen } from './screens/Incident/IncidentCreationScreen';
import { ShzoomAntTheme } from './theme/ShzoomTheme';
import { storageManager } from './utils/storageManager';
import { setNavigateRef } from './utils/navigationRef';
import AuthService from './services/auth';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import Insights from './services/insightsService';

const { Content } = Layout;

function IdleTimeoutGuard() {
  useIdleTimeout();
  return null;
}

function AppRoutes() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const isLoggingOut = useRef(false);

  const handleSessionInvalid = useCallback(async () => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    try {
      await AuthService.clearAuth();
      navigate('/login');
    } finally {
      isLoggingOut.current = false;
    }
  }, [navigate]);

  useEffect(() => {
    setNavigateRef(navigate);
    AuthService.setOnAuthCleared(() => {
      dispatch(clearAuth());
    });
  }, [navigate, dispatch]);

  useEffect(() => {
    if (!isLoggedIn) return;
    dispatch(checkSession());
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const authenticated = await AuthService.isAuthenticated();
        if (!authenticated) {
          handleSessionInvalid();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn, handleSessionInvalid]);

  useEffect(() => {
    const performAutoCleanup = async () => {
      try {
        await storageManager.autoCleanup();
      } catch (error) {
        Insights.trackException(error instanceof Error ? error : new Error(
        typeof error === "object" && error !== null
          ? JSON.stringify(error)
          : String(error)
      ));
      }
    };
    performAutoCleanup();
  }, []);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content>
        {isLoggedIn && <IdleTimeoutGuard />}
        <Routes>
          <Route 
            path="/login" 
            element={isLoggedIn ? <Navigate to="/home" /> : <LoginScreen />} 
          />
          <Route
            path="/auth/redirecting"
            element={isLoggedIn ? <Navigate to="/home" /> : <SsoRedirectingScreen />}
          />
          <Route
            path="/auth/callback"
            element={isLoggedIn ? <Navigate to="/home" /> : <AuthCallbackScreen />}
          />
          <Route
            path="/auth/sso-unavailable"
            element={isLoggedIn ? <Navigate to="/home" /> : <SsoUnavailableScreen />}
          />
          <Route 
            path="/home" 
            element={isLoggedIn ? <HomeScreen /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/vin-scan" 
            element={isLoggedIn ? <Navigate to="/incident/create" /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/incident/create" 
            element={isLoggedIn ? <IncidentCreationScreen /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/" 
            element={<Navigate to={isLoggedIn ? "/home" : "/login"} />} 
          />
        </Routes>
      </Content>
    </Layout>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          An unexpected error occurred. Please refresh the page.
        </div>
      }
    >
      <ConfigProvider theme={ShzoomAntTheme}>
        <Router>
          <AppRoutes />
        </Router>
      </ConfigProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;